// Copyright 2025-2026 NXGN Management, LLC. All Rights Reserved.
import { Customer } from '@/services/customers'

export const formatName = (name: string = ''): string => {
  return name.trim().replace(/\b\w/g, (char) => char.toUpperCase())
}

// eslint-disable-next-line security/detect-unsafe-regex
export const PRACTICE_REGEX = /^[a-z0-9](?:[a-z0-9-_]{0,62}[a-z0-9])?$/

export const normalizeName = (string: string) =>
  (string ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9-_ ]+/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')

export const toDnsLabel = (string: string) => {
  const cleaned = (string ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-+/g, '-')
  return cleaned.slice(0, 63) || 'x'
}

export const withNumericSuffix = (base: string, taken: Set<string>) => {
  if (!taken.has(base)) return base
  let i = 2
  while (taken.has(`${base}-${i}`)) i += 1
  return `${base}-${i}`
}

export const extractPracticeName = (
  displayName: string,
  customerDisplayName?: string
) => {
  if (!displayName) return ''

  if (customerDisplayName) {
    const prefix = `${customerDisplayName} - `
    if (displayName.startsWith(prefix)) {
      return displayName.slice(prefix.length).trim()
    }
  }

  const parts = displayName.split(' - ')
  if (parts.length > 1) return parts[parts.length - 1].trim()

  return displayName.trim()
}

export const getTagColor = (customer?: Customer) =>
  customer?.accountGcn ? 'success' : 'primary'

export const getTagLabel = (customer?: Customer) =>
  customer?.accountGcn ? 'Customer' : 'Internal'

export const makeCompoundValue = (
  gcn: string | undefined,
  name: string | undefined
) => `${String(gcn ?? '').replace(/:/g, '')}::${String(name ?? '')}`

export const parseCompoundValue = (value: string) => {
  const [gcn, ...rest] = value.split('::')
  return { gcn: gcn ?? '', name: rest.join('::') ?? '' }
}

const MIN_CUSTOMER_SHORT_NAME_LENGTH = 3
const MAX_CUSTOMER_SHORT_NAME_LENGTH = 50
const MAX_CUSTOMER_SHORT_NAME_SUFFIX_LENGTH =
  MAX_CUSTOMER_SHORT_NAME_LENGTH - MIN_CUSTOMER_SHORT_NAME_LENGTH - 1

const buildCustomerShortName = (base: string, suffixes: string[]) => {
  const normalizedBase =
    normalizeName(base)
      .replace(/_/g, '-')
      .replace(/^-+|-+$/g, '') || 'customer'
  const normalizedSuffix = suffixes
    .map((suffix) =>
      normalizeName(suffix)
        .replace(/_/g, '-')
        .replace(/^-+|-+$/g, '')
    )
    .filter(Boolean)
    .join('-')
    .slice(-MAX_CUSTOMER_SHORT_NAME_SUFFIX_LENGTH)
    .replace(/^-+|-+$/g, '')
  const separatorLength = normalizedSuffix ? 1 : 0
  const maximumBaseLength =
    MAX_CUSTOMER_SHORT_NAME_LENGTH - normalizedSuffix.length - separatorLength
  const slicedBase = normalizedBase.slice(0, maximumBaseLength)
  const lastHyphen = slicedBase.lastIndexOf('-')
  const shortBase =
    normalizedBase.length > maximumBaseLength &&
    lastHyphen > 0 &&
    lastHyphen >= Math.floor(maximumBaseLength * 0.6)
      ? slicedBase.slice(0, lastHyphen)
      : slicedBase.replace(/-+$/g, '')
  const shortName = normalizedSuffix
    ? `${shortBase}-${normalizedSuffix}`
    : shortBase

  return shortName.padEnd(MIN_CUSTOMER_SHORT_NAME_LENGTH, '0')
}

export const generateUniqueNamePair = (
  base: string,
  takenNames: Set<string>,
  uniqueIdentifier?: string
) => {
  const baseDisplay = String(base ?? '').trim() || 'customer'
  const identifierSuffix = uniqueIdentifier ? [uniqueIdentifier] : []
  let display = baseDisplay
  let short = buildCustomerShortName(baseDisplay, identifierSuffix)
  let i = 1

  const isTaken = (d: string, s: string) =>
    takenNames.has(normalizeName(d)) || takenNames.has(s.toLowerCase())

  if (!isTaken(display, short)) {
    return { display, short }
  }

  while (i <= 1000) {
    display = `${baseDisplay}-${i}`
    short = buildCustomerShortName(baseDisplay, [
      ...identifierSuffix,
      String(i),
    ])
    if (!isTaken(display, short)) return { display, short }
    i += 1
  }

  const ts = Date.now()
  return {
    display: `${baseDisplay}-${ts}`,
    short: buildCustomerShortName(baseDisplay, [
      ...identifierSuffix,
      String(ts),
    ]),
  }
}

export const toMilliseconds = (
  date: Date | string | number | undefined | null
): number => {
  if (date === undefined || date === null) return 0
  if (date instanceof Date) return date.getTime()
  const ms = typeof date === 'number' ? date : Date.parse(String(date))
  return isNaN(ms) ? 0 : ms
}

export const computeMaxContentWidth = (
  numColumns = 5,
  minColumnWidth = 220,
  maxGap = 216
): number => {
  return numColumns * minColumnWidth + (numColumns - 1) * maxGap
}
